/**
 * 交易列表与详情。
 *
 * 控制台是**只读**的：发起支付/请款/退款都走网关 API（那里有幂等键和通道调用）。
 * 同一个能力做两套幂等实现是资损的经典来源，所以这里一个写按钮都没有。
 */

import { useState } from 'react';
import { usePagedList, useAsync } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import { ScopeBanner } from '../components/scope-banner.tsx';
import { Card, CopyId, DetailGrid, ErrorBox, Loading, Modal, Money, StatusBadge, Badge, Empty, Field, TextInput, Select } from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatDateTime, statusLabel, type Tone } from '../lib/format.ts';
import type { Collection } from '../lib/api.ts';
import type { Attempt, Channel, Payment, PaymentDetail } from '../lib/types.ts';

const PAYMENT_STATUSES = [
  'created', 'processing', 'requires_action', 'authorized', 'captured',
  'partially_refunded', 'refunded', 'failed', 'cancelled', 'expired',
];

export function PaymentsPage() {
  const { isChannelScoped } = useSession();
  const [status, setStatus] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [currency, setCurrency] = useState('');
  const [channelId, setChannelId] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  /*
   * 筛选用的通道清单直接来自 `/v1/channels` —— 那个接口已经内建了全部可见性收窄
   * （非平台身份只看得到绑过的通道，被收窄的成员只看得到白名单里的）。
   * 所以下拉里出现的每一条都是当前身份有权筛的，不需要在这里再判一次。
   *
   * 空的 deps 是刻意的：这个清单与筛选状态无关，跟着 channelId 变会打成
   * 「取数 → 重渲染 → 再取数」的循环（712d92e 修过两处同样的东西）。
   */
  const channels = useAsync<Collection<Channel>>('/v1/channels', { query: { limit: 200 } }, []);

  const list = usePagedList<Payment>('/v1/payments', {
    status: status === '' ? null : status,
    order_no: orderNo === '' ? null : orderNo,
    currency: currency === '' ? null : currency.toUpperCase(),
    channel_account_id: channelId === '' ? null : channelId,
  });

  const columns: readonly Column<Payment>[] = [
    { key: 'id', header: '交易号', render: (row) => <CopyId id={row.id} /> },
    { key: 'order', header: '商户订单号', render: (row) => (
      <span className="mono">{row.order_no}</span>
    ) },
    { key: 'merchant', header: '商户', render: (row) => row.merchant_name },
    /*
     * 通道列。商户名和通道名是**两件事**，但它们经常长得很像
     * （线上就有「测试商户-EasyChoice」这个商户，同时存在「EasyChoice Auth」这条通道），
     * 于是没有这一列时，整页商户名会被当成通道读 —— 那 99 笔里有 49 笔实际走的是
     * NovaLand，却没人看得出来。
     *
     * `channel_count > 1` 说明这笔失败重路由过，显示的是成交（或最后一次尝试）的通道，
     * 不是它碰过的全部。不标出来的话，按 A 通道筛却看到行上写着 B，会被当成筛错了。
     * 点进详情能看到逐次 attempt 的完整通道链路。
     */
    { key: 'channel', header: '通道', render: (row) => {
      if (row.channel_label === null) {
        // 一次 attempt 都没有：路由阶段就失败了，这笔从没出过网
        return <span className="cell-muted">未出通道</span>;
      }
      return (
        <span className="row tight">
          <span>{row.channel_label}</span>
          {row.channel_count > 1 && <Badge tone="warn">重路由 {row.channel_count}</Badge>}
        </span>
      );
    } },
    { key: 'amount', header: '金额', numeric: true, render: (row) => (
      <Money value={row.amount} currency={row.currency} />
    ) },
    /*
     * 「已请款」对被收窄到指定通道的成员是**本通道口径**：只有成交在他负责的通道上
     * 才计数，改道成交在别处的那些记 0。
     *
     * 直接画一个 0 会被当成系统算错了 —— 那笔单明明是 captured 状态、金额栏也不是 0。
     * 所以这里对 attempted 的行不显示金额，改成一句话说明它为什么不算他的。
     */
    { key: 'captured', header: '已请款', numeric: true, render: (row) => (
      row.scope_outcome === 'attempted'
        ? <span className="cell-muted">不计入本通道</span>
        : <Money value={row.captured_amount} currency={row.currency} tone="muted" showCurrency={false} />
    ) },
    { key: 'refunded', header: '已退款', numeric: true, render: (row) => (
      row.scope_outcome === 'attempted'
        ? <span className="cell-muted">—</span>
        : <Money value={row.refunded_amount} currency={row.currency} tone={row.refunded_amount === '0' ? 'muted' : 'bad'} showCurrency={false} />
    ) },
    { key: 'status', header: '状态', render: (row) => <StatusBadge status={row.status} /> },
    /*
     * 归属列只对被收窄了可见范围的成员出现（其他人 scope_outcome 恒为 null，
     * 多一列恒定值的表格只会让人猜它是什么意思）。
     *
     * 两种行的区别是这张表最容易被误读的地方：
     *   本通道成交  钱走了你的通道，右边的金额是你的
     *   仅尝试过    你的通道试过但没成，最终成交在别的通道，金额不计入你
     * 后者依然列出来，是因为通道运营查拒付靠的就是这些单。
     */
    ...(isChannelScoped
      ? [{
          key: 'scope',
          header: '通道归属',
          render: (row: Payment) => (
            row.scope_outcome === 'settled'
              ? <Badge tone="neutral">本通道成交</Badge>
              : <Badge tone="warn">仅尝试过</Badge>
          ),
        } satisfies Column<Payment>]
      : []),
    /*
     * 「通道结算」与左边的「状态」是**两回事**，这一列的全部意义就在这个区别上：
     *
     *   状态 = captured        我们向通道请款成功了
     *   通道结算 = 已结算       通道自己确认把这笔钱结出去了
     *
     * 中间这段差距平台**推算不出来**：账期配错、批次结算失败、通道扣了钱没结出来，
     * 三种情况下账本都照常显示「可用」。所以这里只显示通道报上来的事实，
     * 没报就留「未确认」——**绝不因为「按账期该结了」就画成已结算**。
     */
    { key: 'settled', header: '通道结算', render: (row) => {
      if (row.channel_settled_at === null) {
        return <span className="cell-muted">未确认</span>;
      }
      const mismatch =
        row.channel_settled_amount !== null && row.channel_settled_amount !== row.captured_amount;
      return (
        <span className="row tight">
          <span className="ident-chip" data-kind={mismatch ? undefined : 'platform'}>
            {mismatch ? '金额不符' : '已结算'}
          </span>
          <span className="cell-muted mono">{formatDateTime(row.channel_settled_at)}</span>
        </span>
      );
    } },
    { key: 'created', header: '创建时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
  ];

  return (
    <>
      <h1>交易</h1>
      <ScopeBanner context="这些通道的交易。列表里既有成交在你通道上的单，也有你的通道试过、最终成交在别处的单 —— 后者标为「仅尝试过」，金额不计入你，但保留下来是为了让你能查自己通道的拒付。" />

      <div className="filters">
        <Field label="状态">
          <Select
            value={status}
            onChange={setStatus}
            placeholder="全部"
            options={PAYMENT_STATUSES.map((value) => ({ value, label: statusLabel(value) }))}
          />
        </Field>
        {/*
          * 按通道筛的口径是「在这条通道上**试过**」，不要求成交 ——
          * 查拒付正是这个筛选的主要用途，只筛成交的会把要查的单全滤掉。
          * 于是一笔「在 A 被拒、改道 B 成交」的单在两个筛选下都会出现，这是对的。
          */}
        <Field label="通道">
          <Select
            value={channelId}
            onChange={setChannelId}
            placeholder="全部"
            options={(channels.data?.data ?? []).map((ch) => ({ value: ch.id, label: ch.label }))}
          />
        </Field>
        <Field label="商户订单号">
          <TextInput value={orderNo} onChange={setOrderNo} placeholder="精确匹配" />
        </Field>
        <Field label="币种">
          <TextInput value={currency} onChange={setCurrency} placeholder="USD" />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          onRowClick={(row) => { setSelected(row.id); }}
          empty="没有符合条件的交易"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>

      {selected !== null && (
        <PaymentDetailModal id={selected} onClose={() => { setSelected(null); }} />
      )}
    </>
  );
}

function PaymentDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useAsync<PaymentDetail>(`/v1/payments/${id}`);

  return (
    <Modal title="交易详情" onClose={onClose} wide>
      {detail.error !== null ? <ErrorBox error={detail.error} />
        : detail.data === null ? <Loading />
        : (
          <>
            <DetailGrid rows={[
              ['交易号', <CopyId key="id" id={detail.data.id} full />],
              ['商户订单号', <span key="o" className="mono">{detail.data.order_no}</span>],
              ['商户', <span key="m">{detail.data.merchant_name}</span>],
              ['状态', <StatusBadge key="s" status={detail.data.status} />],
              ['金额', <Money key="a" value={detail.data.amount} currency={detail.data.currency} />],
              ['已请款', <Money key="c" value={detail.data.captured_amount} currency={detail.data.currency} />],
              ['已退款', <Money key="r" value={detail.data.refunded_amount} currency={detail.data.currency} />],
              ['请款方式', <span key="cm">{detail.data.capture_method === 'automatic' ? '自动请款' : '手动请款'}</span>],
              detail.data.customer_ref === null ? null
                : ['客户引用', <span key="cr" className="mono">{detail.data.customer_ref}</span>],
              detail.data.description === null ? null
                : ['描述', <span key="d">{detail.data.description}</span>],
              ['创建时间', <span key="t" className="mono">{formatDateTime(detail.data.created_at)}</span>],
              /*
               * 通道结算 —— 与上面的「状态」分开列，因为它们是不同性质的事实。
               * 「状态 captured」是我们请款成功；「通道结算」是通道自己确认钱结出去了。
               * 后者平台推算不出来，只能等通道的结算报表。没报就如实写「通道尚未确认」，
               * 绝不因为账期到了就说已结算。
               */
              ['通道结算', detail.data.channel_settled_at === null
                ? <span key="cs" className="cell-muted">通道尚未确认（不代表未结算，只代表我们还没拿到通道的结算报表）</span>
                : <span key="cs">
                    {formatDateTime(detail.data.channel_settled_at)}
                    {detail.data.channel_settled_amount !== null
                      && detail.data.channel_settled_amount !== detail.data.captured_amount
                      ? <strong> · 实结 <Money value={detail.data.channel_settled_amount}
                          currency={detail.data.currency} /> 与已请款不符，需人工核对</strong>
                      : null}
                  </span>],
              detail.data.channel_batch_id === null ? null
                : ['通道批次号', <span key="cb" className="mono">{detail.data.channel_batch_id}</span>],
            ]} />

            <AttemptsTable attempts={detail.data.attempts} />

            {detail.data.refunds.length > 0 && (
              <div className="stack tight">
                <h3>退款</h3>
                <div className="table-scroll">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>退款号</th><th className="num">金额</th><th>状态</th><th>原因</th><th>时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.data.refunds.map((refund) => (
                        <tr key={refund.id}>
                          <td><CopyId id={refund.id} /></td>
                          <td className="num"><Money value={refund.amount} currency={refund.currency} /></td>
                          <td><StatusBadge status={refund.status} /></td>
                          <td className="cell-muted">{refund.reason ?? '—'}</td>
                          <td className="cell-muted mono">{formatDateTime(refund.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {detail.data.disputes.length > 0 && (
              <div className="stack tight">
                <h3>争议</h3>
                <div className="table-scroll">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>争议号</th><th>阶段</th><th className="num">金额</th><th>状态</th><th>举证截止</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.data.disputes.map((dispute) => (
                        <tr key={dispute.id}>
                          <td><CopyId id={dispute.id} /></td>
                          <td>{dispute.stage}</td>
                          <td className="num"><Money value={dispute.amount} currency={dispute.currency} /></td>
                          <td><StatusBadge status={dispute.status} /></td>
                          <td className="cell-muted mono">{formatDateTime(dispute.due_by)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
    </Modal>
  );
}

/**
 * 尝试链路。
 *
 * 这是排障时最有价值的一块：一笔支付可能在 A 通道被硬拒、重路由到 B 通道成功。
 * 按 `seq` 顺序展开，每一跳的通道、错误码、错误分类都摊开 ——
 * 只看 payment 的最终状态会完全掩盖掉「A 通道正在大量拒付」这个事实。
 */
/**
 * 通道风控结论。目前只有 Stripe Radar 会给，其它通道一律是「—」。
 *
 * ## 为什么把 type 也放出来
 *
 * `blocked` 与 `issuer_declined` 在交易列表上长得一模一样（都是失败），
 * 但处理方式完全相反：前者是 **Radar 自己拦的**，调规则就能放行；
 * 后者是发卡行拒的，改我们这边的任何设置都没用。没有这一栏的时候，
 * 商户只会一遍遍来问「为什么我的客户付不了」，而我们也答不上来。
 *
 * `score` 为 0 与 `score` 缺失是两件事：前者是 Radar 评过、判定极低风险，
 * 后者是这个账户没开 Radar for Fraud Teams。所以这里判的是 undefined，
 * 绝不能写成 `risk.score ? ... : '—'`。
 */
function RiskCell({ risk }: { risk: Attempt['risk_outcome'] }) {
  if (risk === null || risk === undefined) return <span className="cell-muted">—</span>;

  const levelTone: Tone =
    risk.level === 'highest' ? 'bad'
      : risk.level === 'elevated' ? 'warn'
        : risk.level === 'normal' ? 'good'
          : 'neutral';   // not_assessed / unknown：别用颜色暗示一个并不存在的判断
  // blocked = Radar 拦的，和发卡行拒付要分开看
  const typeTone: Tone = risk.type === 'blocked' || risk.type === 'invalid' ? 'bad'
    : risk.type === 'manual_review' ? 'warn'
      : 'neutral';

  return (
    <div className="stack tight">
      <div className="row tight">
        {risk.level !== undefined && <Badge tone={levelTone}>{risk.level}</Badge>}
        {risk.score !== undefined && <span className="mono cell-strong">{risk.score}/100</span>}
        {risk.type !== undefined && <Badge tone={typeTone}>{risk.type}</Badge>}
      </div>
      {risk.reason !== undefined && (
        <span className="cell-muted mono" style={{ fontSize: 11 }}>{risk.reason}</span>
      )}
      {risk.sellerMessage !== undefined && (
        <span className="cell-muted" style={{ fontSize: 11.5 }}>{risk.sellerMessage}</span>
      )}
      {risk.networkStatus !== undefined && risk.networkStatus !== 'approved_by_network' && (
        <span className="cell-muted" style={{ fontSize: 11 }}>卡组织：{risk.networkStatus}</span>
      )}
    </div>
  );
}

function AttemptsTable({ attempts }: { attempts: PaymentDetail['attempts'] }) {
  if (attempts.length === 0) {
    return (
      <div className="stack tight">
        <h3>尝试链路</h3>
        <Empty
          title="没有任何通道尝试"
          hint="这笔支付还没走到通道 —— 通常意味着路由阶段就失败了（没有可用通道、或配置错误）。"
        />
      </div>
    );
  }

  return (
    <div className="stack tight">
      <h3>尝试链路 · {attempts.length} 跳</h3>
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              <th>#</th>
              <th>通道</th>
              <th>状态</th>
              <th className="num">金额</th>
              <th>通道流水号</th>
              <th>错误</th>
              <th>通道风控</th>
              <th>耗时</th>
            </tr>
          </thead>
          <tbody>
            {attempts.map((attempt) => {
              const elapsed =
                attempt.ended_at === null ? null
                  : Math.max(0, new Date(attempt.ended_at).getTime() - new Date(attempt.started_at).getTime());
              return (
                <tr key={attempt.id}>
                  <td className="num">{attempt.seq}</td>
                  <td>
                    <div className="stack tight">
                      <span className="cell-strong">{attempt.channel_code}</span>
                      <span className="cell-muted" style={{ fontSize: 11 }}>{attempt.channel_label}</span>
                    </div>
                  </td>
                  <td><StatusBadge status={attempt.status} /></td>
                  <td className="num"><Money value={attempt.amount} currency={attempt.currency} showCurrency={false} /></td>
                  <td>
                    {attempt.connector_txn_id === null
                      ? <span className="cell-muted">—</span>
                      : <CopyId id={attempt.connector_txn_id} full />}
                  </td>
                  <td className="cell-wrap">
                    {attempt.error_code === null ? (
                      <span className="cell-muted">—</span>
                    ) : (
                      <div className="stack tight">
                        <div className="row tight">
                          <Badge tone={attempt.error_class === 'hard_decline' ? 'bad' : 'warn'}>
                            {attempt.error_class ?? '未分类'}
                          </Badge>
                          <span className="mono">{attempt.error_code}</span>
                        </div>
                        {attempt.error_message !== null && (
                          <span className="cell-muted" style={{ fontSize: 11.5 }}>{attempt.error_message}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="cell-wrap"><RiskCell risk={attempt.risk_outcome} /></td>
                  <td className="cell-muted mono">
                    {elapsed === null ? '进行中' : `${(elapsed / 1000).toFixed(2)}s`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
